import React from 'react';
import {  FiUsers, FiCheckCircle, FiClock, FiShield, FiBarChart2, FiMail, FiCalendar } from 'react-icons/fi';

const SchoolGuide = () => {
  const features = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Information Management',
      description: 'Every school needs a central repository for student data. This includes admission details, academic records, health information, parent contacts, and more. The system should make it easy to access student information, track progress, and generate reports.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Attendance Tracking',
      description: 'Attendance is the foundation of school management. Teachers need to mark attendance quickly—ideally in under 30 seconds. The system should automatically track attendance patterns, generate reports, and notify parents about absences.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Grading and Assessment',
      description: 'From daily assignments to quarterly exams, schools need a robust grading system. The software should handle different assessment types, calculate grades automatically, and generate report cards that are easy to understand. No more manual calculations or formatting.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Parent Communication',
      description: 'Keeping parents informed is critical. The system should enable teachers to share updates, circulars, attendance alerts, and academic progress with parents—without spending hours drafting individual messages. Automated parent communication is a game-changer.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Timetable and Scheduling',
      description: 'Creating and managing timetables is one of the most complex tasks in a school. The software should help create conflict-free schedules, manage teacher assignments, and handle changes easily. No more manual timetable tweaking every week.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Fee Management',
      description: 'Fee collection can be chaotic. The system should automate fee scheduling, send reminders, track payments, and generate receipts—all in one place. Online payment integration is increasingly essential for modern schools.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Save Teachers Hours Every Week',
      description: 'Automate attendance, grading, and communication. Teachers spend 8+ hours weekly on admin work. Good software reduces this to under 2 hours.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Improve Parent Satisfaction',
      description: 'Parents want to know what\'s happening. Regular updates, instant attendance alerts, and easy access to grades make parents happy. Happy parents recommend your school to others.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Make Data-Driven Decisions',
      description: 'Track student progress, identify learning gaps, monitor attendance trends, and make decisions based on real data—not intuition. This improves student outcomes.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Reduce Administrative Errors',
      description: 'Manual data entry leads to errors—wrong grades, missed attendance, incorrect fees. Automation eliminates these errors and saves countless hours of correction.',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  const mistakes = [
    {
      title: 'Choosing Without Teacher Input',
      description: 'Teachers are the primary users of school software. If they don\'t like it, they won\'t use it. Involve teachers in the selection process from the beginning.'
    },
    {
      title: 'Overlooking Mobile Accessibility',
      description: 'Teachers and parents access school systems on their phones. If the software isn\'t mobile-friendly, adoption will be low.'
    },
    {
      title: 'Ignoring Data Migration',
      description: 'Moving data from old systems to a new one is often underestimated. Plan for data migration carefully to avoid losing years of student records.'
    },
    {
      title: 'Not Considering Training',
      description: 'Good software only works if people know how to use it. Include training in your budget and plan. Onboarding teachers takes time—plan for it.'
    },
    {
      title: 'Overcomplicating the System',
      description: 'Schools don\'t need every feature available. Focus on what you actually need. A simple system that works well beats a complex system that frustrates users.'
    },
    {
      title: 'Choosing Without Demo or Trial',
      description: 'Never buy school software without trying it first. Get a demo, let teachers test it, and see if it actually solves your problems.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Choosing the right school management software is one of the most important decisions a K-12 school can make. It affects teachers, parents, administrators, and ultimately, students. The right software makes everything easier. The wrong software creates frustration, wasted time, and unhappy stakeholders.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But with so many options available, how do you choose? What features are actually essential? What mistakes should you avoid? <strong className="text-slate-900">This guide will help you navigate the process.</strong>
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Whether you're a small school with 100 students or a large school with 2,000 students, the principles remain the same. Here's everything you need to know about school management software for K-12 schools in India.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">80%</div>
          <div className="text-sm text-slate-500">Faster admin work with good software</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Parent satisfaction increases</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">2X</div>
          <div className="text-sm text-slate-500">Faster decision-making with real data</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Essential Features for K-12 Schools</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Good School Software Delivers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex items-start gap-3">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Common Mistakes Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Common Mistakes Schools Make (And How to Avoid Them)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {mistakes.map((item, i) => (
            <div key={i} className="bg-rose-50 rounded-xl p-4 border border-rose-200">
              <div className="flex items-start gap-2">
                <span className="text-rose-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decision Checklist */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Your School Management Software Decision Checklist</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Is it easy for teachers to use?</span>
          </div>
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it work on mobile phones?</span>
          </div>
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it automate attendance and grading?</span>
          </div>
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Can parents access updates easily?</span>
          </div>
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it generate report cards automatically?</span>
          </div>
          <div className="flex items-start gap-2 bg-white rounded-lg p-3 border border-indigo-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does the vendor offer proper training?</span>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Why Truyon is the Best Choice for K-12 Schools</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First Design</h4>
            <p className="text-xs text-slate-600">Teachers mark attendance in under 30 seconds</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">🗣️</div>
            <h4 className="font-bold text-slate-900 text-sm">Language Support</h4>
            <p className="text-xs text-slate-600">Multiple Indian languages for teachers and parents</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Offline Mode</h4>
            <p className="text-xs text-slate-600">Works even with intermittent internet connectivity</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Analytics</h4>
            <p className="text-xs text-slate-600">Track student progress and attendance instantly</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Upfront Cost</h4>
            <p className="text-xs text-slate-600">Free to start. Pay only when you grow.</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Simple & Intuitive</h4>
            <p className="text-xs text-slate-600">No training required. Just sign up and start.</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">The Right Software Makes All the Difference</h3>
        <p className="text-indigo-800 leading-relaxed">
          K-12 schools in India face unique challenges—diverse student populations, varying parent expectations, regulatory requirements, and limited resources. The right school management software doesn't just solve these challenges—it transforms how your school operates.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Teachers work efficiently. Parents stay informed. Administrators make better decisions. Students benefit from a more organized, effective learning environment.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon is built for K-12 schools in India.</strong> Simple. Affordable. Effective. Ready to transform your school.
        </p>
      </div>
    </>
  );
};

export default SchoolGuide;